/**
 * Line art animals — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_illustration`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const lineArtAnimalsPack: ToilePack = {
    slug: 'line-art-animals',
    category: 'illustration',
    name: 'Line art animals',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'tiger', label: 'Tiger', subject: "a tiger's face seen head on, whiskers fanned wide and a striped brow"},
        {key: 'wolf', label: 'Wolf', subject: 'a wolf howling with its head raised, a ruffed neck and pointed ears'},
        {key: 'hummingbird', label: 'Hummingbird', subject: 'a hummingbird hovering at a trumpet flower, a long beak and spread tail feathers'},
    ],
    styles: [
        {key: 'continuous', label: 'One line', style: 'a single continuous line drawing, one unbroken stroke of even weight running through the whole animal', palette: 'Black ink on white paper only, no color and no gray tones.', framing: 'Clean closed line art centered on a pure white background with a wide margin, no fills, no shading, no hatching.'},
        {key: 'fine', label: 'Fine', style: 'fine-line drawing, a thin uniform black stroke carrying delicate interior detail in the fur and the eyes', palette: 'Black ink on white paper only, no color and no gray tones.', framing: 'Flat 2D line drawing centered on a pure white background, the full page used with a white margin, no shading.'},
        {key: 'brush', label: 'Brush', style: 'bold brush-stroke drawing, thick tapering black strokes with dry-brush edges and solid black masses', palette: 'Black ink on white paper only, no color and no gray tones.', framing: 'Flat design sheet seen straight on, the drawing isolated on clean white paper, no fills beyond the black strokes.'},
    ],
    directions: ['a horse instead', 'for a tote bag print', 'with flowers around it'],
    seo: {
        title: 'Line art animals: tiger, wolf, hummingbird',
        metaDescription: 'Nine line art animal drawings — tiger, wolf and hummingbird — drawn as one continuous line, fine detail and bold brush strokes, black on white.',
        h1: 'Line art animals: tiger, wolf and hummingbird in three line weights',
        tagline: 'Nine animal drawings, three subjects, three stroke styles — black on white, no fill.',
        intro: 'Line art animals are the drawing that goes on a tote, a tattoo, a bedroom wall — black on white, no fill. These nine draw a tiger, a howling wolf and a hummingbird in a single unbroken line, a fine detailed pen line and a bold brush stroke. Ask for a different animal and the same three treatments draw it again.',
        useCases: [
            'a tattoo design reference',
            'a print for a bedroom or studio wall',
            'a tote bag or t-shirt graphic',
            'a coloring page base',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each drawing is a square 1024×1024 canvas with a white margin, so it prints clean on standard paper or scales up for a wall print.'},
            {q: 'Are these tattoo-ready?', a: 'They are design references, not stencils — a tattoo artist redraws the linework at the placement size before transferring it.'},
            {q: 'Can I request a different animal?', a: 'Yes — the direction field takes any animal, and the same three line treatments draw it again.'},
        ],
    },
}
